/**
 * OpenApiImportModal Component
 * Allows users to import OpenAPI / Swagger 2.0 / 3.x specifications by URL or pasted spec.
 * Features:
 * - Direct parsing of OpenAPI/Swagger specs
 * - Operation preview & configuration (name, risk, HITL mode, response policy)
 * - DELETE method validation according to platform policies
 * - Batch import to selected agent
 */

import React, { useEffect, useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  FileCode,
  Globe,
  Info,
  Layers,
  ShieldAlert,
  Sparkles,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Agent,
  CreateToolDefinitionInput,
  ToolHitlMode,
  ToolPolicies,
  ToolResponsePolicy,
  ToolRiskLevel,
} from '../../types';
import { createToolDefinition, getToolPolicies } from '../../services/api';

// ============================================
// Types
// ============================================

export interface ParsedOperation {
  id: string;
  selected: boolean;
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  toolName: string;
  summary: string;
  description: string;
  endpointUrl: string;
  riskLevel: ToolRiskLevel;
  hitlMode: ToolHitlMode;
  parametersSchema: Record<string, any>;
  outputSchema?: Record<string, any> | null;
  responsePolicy: ToolResponsePolicy;
  isExpanded?: boolean;
}

interface OpenApiImportModalProps {
  open: boolean;
  onClose: () => void;
  agents: Agent[];
  initialAgentId?: string;
  onImportSuccess: (importedCount: number) => void;
}

// ============================================
// Parser Helper Functions
// ============================================

const sanitizeToolName = (input: string): string => {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
};

const extractBaseUrl = (spec: Record<string, any>): string => {
  if (Array.isArray(spec.servers) && spec.servers.length > 0 && spec.servers[0]?.url) {
    let url = spec.servers[0].url;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url.replace(/\/+$/, '');
    }
  }
  if (spec.host) {
    const scheme = (Array.isArray(spec.schemes) && spec.schemes[0]) || 'https';
    const basePath = (spec.basePath || '').replace(/\/+$/, '');
    return `${scheme}://${spec.host}${basePath}`;
  }
  return '';
};

const inferRiskLevel = (method: string): ToolRiskLevel => {
  switch (method.toUpperCase()) {
    case 'GET':
    case 'HEAD':
    case 'OPTIONS':
      return 'read';
    case 'DELETE':
      return 'sensitive';
    default:
      return 'write';
  }
};

const inferHitlMode = (risk: ToolRiskLevel): ToolHitlMode => {
  return risk === 'read' ? 'owner_bypass_allowed' : 'required';
};

const buildParametersSchema = (
  pathParams: any[] = [],
  opParams: any[] = [],
  requestBody: any = null
): Record<string, any> => {
  const properties: Record<string, any> = {};
  const required: string[] = [];

  const allParams = [...pathParams, ...opParams];

  for (const param of allParams) {
    if (!param) continue;
    if (param.in === 'body' && param.schema) {
      // Swagger 2.0 body schema
      if (param.schema.properties) {
        Object.assign(properties, param.schema.properties);
      }
      if (Array.isArray(param.schema.required)) {
        required.push(...param.schema.required);
      }
    } else if (param.name) {
      properties[param.name] = {
        type: param.type || param.schema?.type || 'string',
        description: param.description || '',
      };
      if (param.required) {
        required.push(param.name);
      }
    }
  }

  if (requestBody?.content?.['application/json']?.schema) {
    const rbSchema = requestBody.content['application/json'].schema;
    if (rbSchema.properties) {
      Object.assign(properties, rbSchema.properties);
    }
    if (Array.isArray(rbSchema.required)) {
      required.push(...rbSchema.required);
    }
  }

  return {
    type: 'object',
    properties,
    required: Array.from(new Set(required)),
  };
};

const extractOutputSchema = (operation: Record<string, any>): Record<string, any> | null => {
  const responses = operation.responses || {};
  const okResp = responses['200'] || responses['201'] || responses['default'];
  if (!okResp) return null;

  if (okResp.content?.['application/json']?.schema) {
    return okResp.content['application/json'].schema;
  }
  if (okResp.schema) {
    return okResp.schema;
  }
  return null;
};

const suggestResponsePolicy = (outputSchema: Record<string, any> | null): ToolResponsePolicy => {
  let defaultResultPath: string | undefined = undefined;
  const allowedPaths: string[] = [];

  if (outputSchema) {
    if (outputSchema.type === 'array' || outputSchema.items) {
      defaultResultPath = '';
      if (outputSchema.items?.properties) {
        allowedPaths.push(...Object.keys(outputSchema.items.properties).map((k) => `[].${k}`));
      }
    } else if (outputSchema.properties) {
      const keys = Object.keys(outputSchema.properties);
      for (const k of keys) {
        const prop = outputSchema.properties[k];
        if (prop.type === 'array' || prop.items) {
          if (!defaultResultPath) defaultResultPath = k;
          if (prop.items?.properties) {
            allowedPaths.push(...Object.keys(prop.items.properties).map((subKey) => `${k}[].${subKey}`));
          }
        } else {
          allowedPaths.push(k);
        }
      }
    }
  }

  return {
    allowedJsonPaths: allowedPaths.length > 0 ? allowedPaths.slice(0, 15) : undefined,
    defaultResultPath: defaultResultPath || undefined,
    redactPaths: ['password', 'token', 'secret', 'apiKey', 'authorization'],
    maxItems: 20,
    maxResponseBytes: 20000,
  };
};

export const OpenApiImportModal: React.FC<OpenApiImportModalProps> = ({
  open,
  onClose,
  agents,
  initialAgentId,
  onImportSuccess,
}) => {
  const { t } = useTranslation();

  // Dialog Steps: 0 = Input Source, 1 = Operations Review, 2 = Importing
  const [activeStep, setActiveStep] = useState<number>(0);
  const [inputTab, setInputTab] = useState<number>(0); // 0 = URL, 1 = Spec Text

  // Input States
  const [specUrl, setSpecUrl] = useState('');
  const [rawSpec, setRawSpec] = useState('');
  const [baseUrlOverride, setBaseUrlOverride] = useState('');
  const [selectedAgentPublicId, setSelectedAgentPublicId] = useState<string>(initialAgentId || '');

  // Loading & Error States
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedOperations, setParsedOperations] = useState<ParsedOperation[]>([]);
  const [detectedTitle, setDetectedTitle] = useState('');

  // Policies
  const [policies, setPolicies] = useState<ToolPolicies>({
    allowDeleteMethod: false,
  });

  // Filter & Search in step 1
  const [methodFilter, setMethodFilter] = useState<string>('ALL');
  const [searchFilter, setSearchFilter] = useState<string>('');

  // Import Execution States
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{
    current: number;
    total: number;
    success: number;
    failed: number;
    errors: string[];
  }>({ current: 0, total: 0, success: 0, failed: 0, errors: [] });

  // Load Policies
  useEffect(() => {
    if (open) {
      void (async () => {
        const res = await getToolPolicies();
        if (res.success && res.data) {
          setPolicies(res.data);
        }
      })();
    }
  }, [open]);

  useEffect(() => {
    if (initialAgentId) {
      setSelectedAgentPublicId(initialAgentId);
    }
  }, [initialAgentId]);

  const handleReset = () => {
    setActiveStep(0);
    setSpecUrl('');
    setRawSpec('');
    setBaseUrlOverride('');
    setParsedOperations([]);
    setParseError(null);
    setDetectedTitle('');
    setIsImporting(false);
    setImportProgress({ current: 0, total: 0, success: 0, failed: 0, errors: [] });
  };

  const handleClose = () => {
    if (isImporting) return;
    handleReset();
    onClose();
  };

  // Parse Spec
  const handleParse = async () => {
    setIsParsing(true);
    setParseError(null);

    let specObject: Record<string, any> | null = null;

    try {
      if (inputTab === 0) {
        // Fetch from URL
        const trimmedUrl = specUrl.trim();
        if (!trimmedUrl) {
          throw new Error('Please enter a valid OpenAPI or Swagger spec URL.');
        }
        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
          throw new Error('URL must start with http:// or https://');
        }

        const res = await fetch(trimmedUrl);
        if (!res.ok) {
          throw new Error(`Failed to fetch spec (${res.status} ${res.statusText})`);
        }
        const text = await res.text();
        specObject = JSON.parse(text);
      } else {
        // Parse raw JSON text
        const trimmedSpec = rawSpec.trim();
        if (!trimmedSpec) {
          throw new Error('Please paste an OpenAPI or Swagger JSON specification.');
        }
        specObject = JSON.parse(trimmedSpec);
      }

      if (!specObject || typeof specObject !== 'object') {
        throw new Error('Invalid OpenAPI/Swagger structure.');
      }

      const isSwagger2 = specObject.swagger?.startsWith('2.');
      const isOpenApi3 = specObject.openapi?.startsWith('3.');

      if (!isSwagger2 && !isOpenApi3) {
        throw new Error('Unsupported specification. Only Swagger 2.0 and OpenAPI 3.x are supported.');
      }

      const title = specObject.info?.title || 'OpenAPI Service';
      setDetectedTitle(title);

      const discoveredBaseUrl = extractBaseUrl(specObject);
      const effectiveBaseUrl = baseUrlOverride.trim() || discoveredBaseUrl;

      const paths = specObject.paths || {};
      const operations: ParsedOperation[] = [];

      const methodsList: Array<'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'> = [
        'GET',
        'POST',
        'PUT',
        'PATCH',
        'DELETE',
      ];

      for (const [pathKey, pathItem] of Object.entries<any>(paths)) {
        if (!pathItem || typeof pathItem !== 'object') continue;

        const pathParams = Array.isArray(pathItem.parameters) ? pathItem.parameters : [];

        for (const m of methodsList) {
          const op = pathItem[m.toLowerCase()];
          if (!op) continue;

          const baseName =
            op.operationId || `${m.toLowerCase()}_${pathKey.replace(/[^a-zA-Z0-9]/g, '_')}`;
          const toolName = sanitizeToolName(baseName);
          const summary = op.summary || `${m} ${pathKey}`;
          const description = op.description || op.summary || '';
          const riskLevel = inferRiskLevel(m);
          const hitlMode = inferHitlMode(riskLevel);

          const paramsSchema = buildParametersSchema(
            pathParams,
            op.parameters || [],
            op.requestBody
          );
          const outputSchema = extractOutputSchema(op);
          const responsePolicy = suggestResponsePolicy(outputSchema);

          // Calculate full endpoint URL
          let fullEndpoint = pathKey;
          if (effectiveBaseUrl) {
            fullEndpoint = `${effectiveBaseUrl.replace(/\/+$/, '')}/${pathKey.replace(/^\/+/, '')}`;
          }

          const isDeleteDisabled = m === 'DELETE' && !policies.allowDeleteMethod;

          operations.push({
            id: `${m}-${pathKey}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            selected: !isDeleteDisabled,
            path: pathKey,
            method: m,
            toolName: toolName || `tool_${operations.length + 1}`,
            summary,
            description,
            endpointUrl: fullEndpoint,
            riskLevel,
            hitlMode,
            parametersSchema: paramsSchema,
            outputSchema,
            responsePolicy,
            isExpanded: false,
          });
        }
      }

      if (operations.length === 0) {
        throw new Error('No valid HTTP operations found in the specification.');
      }

      setParsedOperations(operations);
      setActiveStep(1);
    } catch (err: any) {
      setParseError(err.message || 'Failed to parse specification. Ensure it is valid JSON.');
    } finally {
      setIsParsing(false);
    }
  };

  // Quick sample loader
  const handleLoadSample = () => {
    setInputTab(0);
    setSpecUrl('https://petstore.swagger.io/v2/swagger.json');
  };

  // Operation updates in Step 1
  const handleToggleSelectAll = (checked: boolean) => {
    setParsedOperations((ops) =>
      ops.map((op) => {
        if (op.method === 'DELETE' && !policies.allowDeleteMethod) {
          return { ...op, selected: false };
        }
        return { ...op, selected: checked };
      })
    );
  };

  const handleToggleSelectOp = (id: string, checked: boolean) => {
    setParsedOperations((ops) =>
      ops.map((op) => (op.id === id ? { ...op, selected: checked } : op))
    );
  };

  const handleUpdateOpField = (id: string, field: keyof ParsedOperation, value: any) => {
    setParsedOperations((ops) =>
      ops.map((op) => (op.id === id ? { ...op, [field]: value } : op))
    );
  };

  const handleUpdateOpPolicy = (
    id: string,
    field: keyof ToolResponsePolicy,
    value: any
  ) => {
    setParsedOperations((ops) =>
      ops.map((op) =>
        op.id === id
          ? {
              ...op,
              responsePolicy: {
                ...op.responsePolicy,
                [field]: value,
              },
            }
          : op
      )
    );
  };

  const handleToggleExpandOp = (id: string) => {
    setParsedOperations((ops) =>
      ops.map((op) => (op.id === id ? { ...op, isExpanded: !op.isExpanded } : op))
    );
  };

  // Filtered operations for display
  const filteredOperations = useMemo(() => {
    return parsedOperations.filter((op) => {
      if (methodFilter !== 'ALL' && op.method !== methodFilter) return false;
      if (searchFilter.trim()) {
        const query = searchFilter.toLowerCase();
        return (
          op.toolName.toLowerCase().includes(query) ||
          op.path.toLowerCase().includes(query) ||
          op.summary.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [parsedOperations, methodFilter, searchFilter]);

  const selectedCount = parsedOperations.filter((op) => op.selected).length;

  // Execute Batch Import
  const handleExecuteImport = async () => {
    const selectedOps = parsedOperations.filter((op) => op.selected);
    if (selectedOps.length === 0) return;

    setIsImporting(true);
    setActiveStep(2);

    const progress = {
      current: 0,
      total: selectedOps.length,
      success: 0,
      failed: 0,
      errors: [] as string[],
    };
    setImportProgress(progress);

    for (let i = 0; i < selectedOps.length; i++) {
      const op = selectedOps[i];
      progress.current = i + 1;

      try {
        const input: CreateToolDefinitionInput = {
          name: op.toolName,
          description: op.description || op.summary || `${op.method} ${op.path}`,
          endpoint_url: op.endpointUrl.startsWith('http')
            ? op.endpointUrl
            : `https://${op.endpointUrl.replace(/^\/+/, '')}`,
          method: op.method,
          auth_type: 'none',
          risk_level: op.riskLevel,
          hitl_mode: op.hitlMode,
          parameters_schema: op.parametersSchema,
          response_policy: op.responsePolicy,
          agent_public_id: selectedAgentPublicId || undefined,
          status: 'active',
          timeout_ms: 15000,
        };

        const res = await createToolDefinition(input);
        if (res.success) {
          progress.success += 1;
        } else {
          progress.failed += 1;
          progress.errors.push(`${op.toolName}: ${res.error || 'Failed to create tool'}`);
        }
      } catch (err: any) {
        progress.failed += 1;
        progress.errors.push(`${op.toolName}: ${err.message || 'Unknown error'}`);
      }

      setImportProgress({ ...progress });
    }

    setIsImporting(false);
  };

  const handleFinish = () => {
    onImportSuccess(importProgress.success);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: 'bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 rounded-xl shadow-2xl',
        sx: { minHeight: '620px' },
      }}
    >
      {/* Modal Header */}
      <DialogTitle className="flex items-center justify-between border-b border-gray-200 dark:border-slate-800 px-6 py-4">
        <Box className="flex items-center gap-2.5">
          <Box className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
            <Layers size={22} />
          </Box>
          <Box>
            <Typography variant="h6" className="font-semibold leading-tight">
              {t('tools.importOpenApi.title', 'Import Tools from OpenAPI / Swagger')}
            </Typography>
            <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
              {activeStep === 0
                ? t('tools.importOpenApi.step1Subtitle', 'Provide an OpenAPI or Swagger spec source')
                : activeStep === 1
                ? t('tools.importOpenApi.step2Subtitle', 'Review operations, response policies, and HITL settings')
                : t('tools.importOpenApi.step3Subtitle', 'Import progress')}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} disabled={isImporting} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      {/* Modal Content */}
      <DialogContent className="p-6">
        {/* Step 0: Input Source */}
        {activeStep === 0 && (
          <Box className="space-y-5">
            {parseError && (
              <Alert severity="error" className="rounded-lg">
                {parseError}
              </Alert>
            )}

            {/* Target Agent Selector */}
            <FormControl fullWidth size="small">
              <InputLabel id="target-agent-label">
                {t('tools.fields.assignAgent', 'Assign to Agent')}
              </InputLabel>
              <Select
                labelId="target-agent-label"
                value={selectedAgentPublicId}
                label={t('tools.fields.assignAgent', 'Assign to Agent')}
                onChange={(e) => setSelectedAgentPublicId(e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('tools.assignAgent.shared', 'Unassigned (Shared / Global)')}</em>
                </MenuItem>
                {agents.map((agent) => (
                  <MenuItem key={agent.publicId} value={agent.publicId}>
                    {agent.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Base URL Override */}
            <TextField
              fullWidth
              size="small"
              label={t('tools.importOpenApi.baseUrlOverride', 'Base URL Override (Optional)')}
              placeholder="https://api.example.com/v1"
              value={baseUrlOverride}
              onChange={(e) => setBaseUrlOverride(e.target.value)}
              helperText={t(
                'tools.importOpenApi.baseUrlHelper',
                'Leave blank to automatically extract host/servers from specification.'
              )}
            />

            {/* Source Tabs */}
            <Box className="border border-gray-200 dark:border-slate-800 rounded-lg overflow-hidden">
              <Tabs
                value={inputTab}
                onChange={(_, val) => setInputTab(val)}
                className="border-b border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/40"
              >
                <Tab
                  icon={<Globe size={16} />}
                  iconPosition="start"
                  label={t('tools.importOpenApi.tabUrl', 'URL')}
                />
                <Tab
                  icon={<FileCode size={16} />}
                  iconPosition="start"
                  label={t('tools.importOpenApi.tabPaste', 'Paste Spec JSON')}
                />
              </Tabs>

              <Box className="p-4">
                {inputTab === 0 ? (
                  <Box className="space-y-3">
                    <TextField
                      fullWidth
                      size="small"
                      label={t('tools.importOpenApi.urlLabel', 'OpenAPI / Swagger Spec URL')}
                      placeholder="https://petstore.swagger.io/v2/swagger.json"
                      value={specUrl}
                      onChange={(e) => setSpecUrl(e.target.value)}
                    />
                    <Box className="flex justify-between items-center">
                      <Button
                        size="small"
                        variant="text"
                        onClick={handleLoadSample}
                        className="text-xs text-indigo-600 dark:text-indigo-400 lowercase hover:underline"
                      >
                        ⚡ load petstore sample url
                      </Button>
                      <Typography variant="caption" className="text-gray-400">
                        Supports OpenAPI 3.0 / 3.1 & Swagger 2.0
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box className="space-y-2">
                    <TextField
                      fullWidth
                      multiline
                      rows={9}
                      label={t('tools.importOpenApi.rawSpecLabel', 'Paste OpenAPI Specification (JSON)')}
                      placeholder='{\n  "openapi": "3.0.0",\n  "info": { "title": "My API", "version": "1.0.0" },\n  "paths": { ... }\n}'
                      value={rawSpec}
                      onChange={(e) => setRawSpec(e.target.value)}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontFamily: 'monospace',
                          fontSize: '0.825rem',
                        },
                      }}
                    />
                    <Typography variant="caption" className="text-gray-400">
                      Ensure pasted text is a valid JSON OpenAPI or Swagger spec.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Hint alert */}
            <Alert severity="info" icon={<Info size={18} />} className="text-xs rounded-lg">
              {t(
                'tools.importOpenApi.importInfo',
                'Discovered endpoints will be mapped to tool names with automatically generated input schemas and suggested response policies. You can customize them before saving.'
              )}
            </Alert>
          </Box>
        )}

        {/* Step 1: Review & Customize Operations */}
        {activeStep === 1 && (
          <Box className="space-y-4">
            {/* Header / Summary Bar */}
            <Box className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-slate-800/60 rounded-lg border border-gray-200 dark:border-slate-700">
              <Box>
                <Typography variant="subtitle2" className="font-semibold">
                  {detectedTitle}
                </Typography>
                <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                  Found {parsedOperations.length} operations • {selectedCount} selected for import
                </Typography>
              </Box>

              {/* Filters */}
              <Box className="flex items-center gap-2">
                <TextField
                  size="small"
                  placeholder="Search operations..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  sx={{ width: 180, '& input': { fontSize: '0.8rem', py: 0.75 } }}
                />
                <Select
                  size="small"
                  value={methodFilter}
                  onChange={(e) => setMethodFilter(e.target.value)}
                  sx={{ minWidth: 90, fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.75 } }}
                >
                  <MenuItem value="ALL">ALL</MenuItem>
                  <MenuItem value="GET">GET</MenuItem>
                  <MenuItem value="POST">POST</MenuItem>
                  <MenuItem value="PUT">PUT</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                </Select>
              </Box>
            </Box>

            {/* Select All Row */}
            <Box className="flex items-center justify-between px-2">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={
                      selectedCount > 0 &&
                      selectedCount ===
                        parsedOperations.filter((op) => op.method !== 'DELETE' || policies.allowDeleteMethod).length
                    }
                    indeterminate={
                      selectedCount > 0 &&
                      selectedCount <
                        parsedOperations.filter((op) => op.method !== 'DELETE' || policies.allowDeleteMethod).length
                    }
                    onChange={(e) => handleToggleSelectAll(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" className="font-medium text-xs text-gray-700 dark:text-slate-300">
                    Select All Operations
                  </Typography>
                }
              />
              {!policies.allowDeleteMethod && (
                <Typography variant="caption" className="text-amber-600 dark:text-amber-400 flex items-center gap-1 text-xs">
                  <ShieldAlert size={14} /> DELETE method is disabled by platform policy
                </Typography>
              )}
            </Box>

            {/* Operations List */}
            <Box className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
              {filteredOperations.map((op) => {
                const isDeleteDisabled = op.method === 'DELETE' && !policies.allowDeleteMethod;

                return (
                  <Card
                    key={op.id}
                    variant="outlined"
                    className={`border transition-all ${
                      op.selected
                        ? 'border-indigo-400/60 dark:border-indigo-500/60 bg-indigo-50/20 dark:bg-indigo-950/20'
                        : 'border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900/60'
                    }`}
                  >
                    <CardContent className="p-3.5 space-y-2.5">
                      {/* Operation Header */}
                      <Box className="flex items-center justify-between gap-3">
                        <Box className="flex items-center gap-2.5 min-w-0">
                          <Checkbox
                            size="small"
                            checked={op.selected}
                            disabled={isDeleteDisabled}
                            onChange={(e) => handleToggleSelectOp(op.id, e.target.checked)}
                          />
                          <Chip
                            label={op.method}
                            size="small"
                            className={`font-bold text-xs ${
                              op.method === 'GET'
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                                : op.method === 'POST'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                                : op.method === 'PUT' || op.method === 'PATCH'
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
                            }`}
                          />
                          <Typography
                            variant="subtitle2"
                            className="font-mono font-medium text-xs truncate max-w-sm"
                            title={op.endpointUrl}
                          >
                            {op.path}
                          </Typography>
                        </Box>

                        <Box className="flex items-center gap-2 flex-shrink-0">
                          {/* Risk Badge */}
                          <Chip
                            label={op.riskLevel}
                            size="small"
                            className={`text-xs capitalize ${
                              op.riskLevel === 'read'
                                ? 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300'
                                : op.riskLevel === 'write'
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300'
                            }`}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleToggleExpandOp(op.id)}
                            className="text-gray-400"
                          >
                            {op.isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Tool Name & Endpoint Preview */}
                      <Box className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-9">
                        <TextField
                          size="small"
                          label="Tool Name"
                          value={op.toolName}
                          onChange={(e) =>
                            handleUpdateOpField(op.id, 'toolName', sanitizeToolName(e.target.value))
                          }
                          inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                        />
                        <TextField
                          size="small"
                          label="Full Endpoint URL"
                          value={op.endpointUrl}
                          onChange={(e) => handleUpdateOpField(op.id, 'endpointUrl', e.target.value)}
                          inputProps={{ style: { fontSize: '0.75rem' } }}
                        />
                      </Box>

                      {isDeleteDisabled && (
                        <Alert severity="warning" className="text-xs py-1 pl-9">
                          DELETE method requires AppAI admin policy TOOL_POLICY_ALLOW_DELETE_METHOD.
                        </Alert>
                      )}

                      {/* Collapsible Details: Risk, HITL & Response Policy */}
                      <Collapse in={op.isExpanded} timeout="auto">
                        <Box className="mt-3 pl-9 pt-3 border-t border-gray-100 dark:border-slate-800 space-y-4">
                          {/* Risk & HITL Config */}
                          <Box className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <FormControl size="small" fullWidth>
                              <InputLabel>Risk Level</InputLabel>
                              <Select
                                value={op.riskLevel}
                                label="Risk Level"
                                onChange={(e) => {
                                  const newRisk = e.target.value as ToolRiskLevel;
                                  handleUpdateOpField(op.id, 'riskLevel', newRisk);
                                  handleUpdateOpField(op.id, 'hitlMode', inferHitlMode(newRisk));
                                }}
                              >
                                <MenuItem value="read">Read (Safe GET)</MenuItem>
                                <MenuItem value="write">Write (Mutations)</MenuItem>
                                <MenuItem value="sensitive">Sensitive (Privileged)</MenuItem>
                              </Select>
                            </FormControl>

                            <Box className="flex items-center justify-between p-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/30">
                              <Box>
                                <Typography variant="caption" className="font-semibold block text-xs">
                                  Require Human Confirmation (HITL)
                                </Typography>
                                <Typography variant="caption" className="text-gray-400 text-[10px]">
                                  {op.hitlMode === 'required'
                                    ? 'Agent must ask user confirmation before calling'
                                    : 'Agent may execute directly with idempotency'}
                                </Typography>
                              </Box>
                              <Switch
                                size="small"
                                checked={op.hitlMode === 'required'}
                                onChange={(e) =>
                                  handleUpdateOpField(
                                    op.id,
                                    'hitlMode',
                                    e.target.checked ? 'required' : 'owner_bypass_allowed'
                                  )
                                }
                              />
                            </Box>
                          </Box>

                          {/* Response Policy Form */}
                          <Box className="space-y-2 p-3 bg-gray-50 dark:bg-slate-800/40 rounded-lg border border-gray-200 dark:border-slate-700">
                            <Typography variant="caption" className="font-semibold text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                              <Sparkles size={13} /> Form-based Response Policy (Filter Model Context)
                            </Typography>

                            <Box className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <TextField
                                size="small"
                                label="Allowed JSON Paths"
                                placeholder="items[].id, items[].name, total"
                                value={(op.responsePolicy.allowedJsonPaths || []).join(', ')}
                                onChange={(e) =>
                                  handleUpdateOpPolicy(
                                    op.id,
                                    'allowedJsonPaths',
                                    e.target.value
                                      ? e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                                      : undefined
                                  )
                                }
                                helperText="Comma-separated field paths to preserve"
                                inputProps={{ style: { fontSize: '0.75rem' } }}
                              />
                              <TextField
                                size="small"
                                label="Default Result Array Path"
                                placeholder="e.g. items or data"
                                value={op.responsePolicy.defaultResultPath || ''}
                                onChange={(e) =>
                                  handleUpdateOpPolicy(op.id, 'defaultResultPath', e.target.value || undefined)
                                }
                                helperText="Root path if response is wrapped in an object"
                                inputProps={{ style: { fontSize: '0.75rem' } }}
                              />
                            </Box>

                            <Box className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              <TextField
                                size="small"
                                label="Redact Paths"
                                placeholder="password, token, secret"
                                value={(op.responsePolicy.redactPaths || []).join(', ')}
                                onChange={(e) =>
                                  handleUpdateOpPolicy(
                                    op.id,
                                    'redactPaths',
                                    e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                                  )
                                }
                                inputProps={{ style: { fontSize: '0.75rem' } }}
                              />
                              <TextField
                                size="small"
                                type="number"
                                label="Max Items"
                                value={op.responsePolicy.maxItems || 20}
                                onChange={(e) =>
                                  handleUpdateOpPolicy(op.id, 'maxItems', parseInt(e.target.value, 10) || 20)
                                }
                                inputProps={{ style: { fontSize: '0.75rem' } }}
                              />
                              <TextField
                                size="small"
                                type="number"
                                label="Max Bytes"
                                value={op.responsePolicy.maxResponseBytes || 20000}
                                onChange={(e) =>
                                  handleUpdateOpPolicy(
                                    op.id,
                                    'maxResponseBytes',
                                    parseInt(e.target.value, 10) || 20000
                                  )
                                }
                                inputProps={{ style: { fontSize: '0.75rem' } }}
                              />
                            </Box>
                          </Box>

                          {/* Schema Previews */}
                          <Box className="flex gap-2">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                alert(JSON.stringify(op.parametersSchema, null, 2));
                              }}
                              className="text-xs"
                            >
                              Inspect Input Schema
                            </Button>
                            {op.outputSchema && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  alert(JSON.stringify(op.outputSchema, null, 2));
                                }}
                                className="text-xs"
                              >
                                Inspect Output Schema
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                );
              })}

              {filteredOperations.length === 0 && (
                <Box className="text-center py-8 text-gray-500">
                  <Typography variant="body2">No operations match your search criteria.</Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Step 2: Import Progress & Results */}
        {activeStep === 2 && (
          <Box className="space-y-6 py-6 text-center">
            {isImporting ? (
              <Box className="space-y-4">
                <CircularProgress size={48} className="text-indigo-600" />
                <Typography variant="h6" className="font-semibold">
                  Importing Tools ({importProgress.current} / {importProgress.total})
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  Creating external tool definitions and configuring policies...
                </Typography>
              </Box>
            ) : (
              <Box className="space-y-4">
                <Box className="w-16 h-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <Check size={32} />
                </Box>
                <Typography variant="h6" className="font-semibold">
                  Import Completed!
                </Typography>
                <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
                  Successfully imported <strong>{importProgress.success}</strong> tools.
                  {importProgress.failed > 0 && (
                    <span className="text-red-500 block mt-1">
                      {importProgress.failed} tools failed to import.
                    </span>
                  )}
                </Typography>

                {importProgress.errors.length > 0 && (
                  <Alert severity="warning" className="text-left text-xs max-h-40 overflow-y-auto mt-4">
                    <ul className="list-disc pl-4 space-y-1">
                      {importProgress.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Modal Actions */}
      <DialogActions className="border-t border-gray-200 dark:border-slate-800 px-6 py-4 justify-between">
        {activeStep === 0 && (
          <>
            <Button onClick={handleClose} variant="outlined" color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleParse}
              variant="contained"
              disabled={isParsing || (inputTab === 0 ? !specUrl.trim() : !rawSpec.trim())}
              startIcon={isParsing ? <CircularProgress size={16} /> : <Sparkles size={16} />}
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              {isParsing ? 'Parsing...' : 'Parse Specification'}
            </Button>
          </>
        )}

        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)} variant="outlined" color="inherit">
              Back to Source
            </Button>
            <Button
              onClick={handleExecuteImport}
              variant="contained"
              disabled={selectedCount === 0}
              startIcon={<Download size={16} />}
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Import {selectedCount} Selected Tools
            </Button>
          </>
        )}

        {activeStep === 2 && (
          <Box className="w-full flex justify-end">
            <Button
              onClick={handleFinish}
              variant="contained"
              disabled={isImporting}
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Done
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};
